import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { Shield, AlertTriangle, Info, Save, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface FraudSettingsData {
  risk_threshold: number
  auto_block: boolean
  auto_approve_threshold: number
  velocity_check_minutes: number
  max_attempts_per_ip: number
  patterns: {
    sequential_phone: boolean
    datacenter_ip: boolean
    disposable_email: boolean
    zip_concentration: boolean
    velocity_abuse: boolean
  }
}

export function FraudSettings() {
  const [isLoading, setIsLoading] = useState(false)
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty }
  } = useForm<FraudSettingsData>({
    defaultValues: {
      risk_threshold: 75,
      auto_block: true,
      auto_approve_threshold: 25,
      velocity_check_minutes: 5,
      max_attempts_per_ip: 10,
      patterns: {
        sequential_phone: true,
        datacenter_ip: true,
        disposable_email: true,
        zip_concentration: true,
        velocity_abuse: true
      }
    }
  })
  
  const riskThreshold = watch('risk_threshold')
  const autoApproveThreshold = watch('auto_approve_threshold')
  const patterns = watch('patterns')
  
  const onSubmit = async (data: FraudSettingsData) => {
    setIsLoading(true)
    try {
      // Here would be the API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      
      toast.success('Fraud detection settings updated')
    } catch (error) {
      toast.error('Failed to update settings')
    } finally {
      setIsLoading(false)
    }
  }
  
  const resetToDefaults = () => {
    reset({
      risk_threshold: 75,
      auto_block: true,
      auto_approve_threshold: 25,
      velocity_check_minutes: 5,
      max_attempts_per_ip: 10,
      patterns: {
        sequential_phone: true,
        datacenter_ip: true,
        disposable_email: true,
        zip_concentration: true,
        velocity_abuse: true
      }
    })
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Fraud Detection Settings
        </h2>
        <p className="text-sm text-muted-foreground">
          Configure fraud detection thresholds and patterns
        </p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Risk Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Risk Thresholds
            </CardTitle>
            <CardDescription>
              Set the risk score thresholds for automatic actions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Auto-Block Threshold */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Auto-Block Threshold</Label>
                <span className="text-sm font-medium text-destructive">
                  {riskThreshold}%
                </span>
              </div>
              <Slider
                value={[riskThreshold]}
                onValueChange={(value) => setValue('risk_threshold', value[0], { shouldDirty: true })}
                min={50}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>50% (Lenient)</span>
                <span>100% (Strict)</span>
              </div>
            </div>
            
            {/* Auto-Approve Threshold */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Auto-Approve Threshold</Label>
                <span className="text-sm font-medium text-green-600">
                  {autoApproveThreshold}%
                </span>
              </div>
              <Slider
                value={[autoApproveThreshold]}
                onValueChange={(value) => setValue('auto_approve_threshold', value[0], { shouldDirty: true })}
                min={0}
                max={50}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0% (Manual Only)</span>
                <span>50% (Permissive)</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Detection Patterns */}
        <Card>
          <CardHeader>
            <CardTitle>Detection Patterns</CardTitle>
            <CardDescription>
              Enable or disable specific fraud detection patterns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Sequential Phone Numbers</Label>
                  <p className="text-sm text-muted-foreground">
                    Detect patterns like 1234, 5555, 0000
                  </p>
                </div>
                <Switch
                  checked={patterns.sequential_phone}
                  onCheckedChange={(checked) => 
                    setValue('patterns.sequential_phone', checked, { shouldDirty: true })
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Datacenter IPs</Label>
                  <p className="text-sm text-muted-foreground">
                    Block VPN and hosting provider IPs
                  </p>
                </div>
                <Switch
                  checked={patterns.datacenter_ip}
                  onCheckedChange={(checked) => 
                    setValue('patterns.datacenter_ip', checked, { shouldDirty: true })
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Disposable Emails</Label>
                  <p className="text-sm text-muted-foreground">
                    Detect temporary email services
                  </p>
                </div>
                <Switch
                  checked={patterns.disposable_email}
                  onCheckedChange={(checked) => 
                    setValue('patterns.disposable_email', checked, { shouldDirty: true })
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">ZIP Code Concentration</Label>
                  <p className="text-sm text-muted-foreground">
                    Detect unusual ZIP code patterns
                  </p>
                </div>
                <Switch
                  checked={patterns.zip_concentration}
                  onCheckedChange={(checked) => 
                    setValue('patterns.zip_concentration', checked, { shouldDirty: true })
                  }
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Velocity Abuse</Label>
                  <p className="text-sm text-muted-foreground">
                    Detect rapid submission patterns
                  </p>
                </div>
                <Switch
                  checked={patterns.velocity_abuse}
                  onCheckedChange={(checked) => 
                    setValue('patterns.velocity_abuse', checked, { shouldDirty: true })
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Velocity Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Velocity Checks</CardTitle>
            <CardDescription>
              Configure velocity abuse detection parameters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="velocity_check_minutes">Check Window (minutes)</Label>
                <Input
                  id="velocity_check_minutes"
                  {...register('velocity_check_minutes', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="60"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="max_attempts_per_ip">Max Attempts per IP</Label>
                <Input
                  id="max_attempts_per_ip"
                  {...register('max_attempts_per_ip', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  max="100"
                />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Warning Message */}
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Changes to fraud detection settings will apply immediately to all new leads.
            Existing leads will not be re-evaluated.
          </AlertDescription>
        </Alert>
        
        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={resetToDefaults}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button
            type="submit"
            disabled={!isDirty || isLoading}
          >
            {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />}
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  )
}